import * as React from 'react'
import { Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { formatTime12Hour } from '@/lib/time-utils'

type Period = 'AM' | 'PM'

interface TimePickerProps {
  /** Time value in HH:mm format (24-hour) */
  value?: string
  /** Callback when time changes, receives HH:mm format */
  onChange: (time: string) => void
  /** Disable the input */
  disabled?: boolean
  /** Placeholder text when no value selected */
  placeholder?: string
  /** Additional CSS classes */
  className?: string
  /** Minutes interval (default: 1) */
  minuteInterval?: number
  /**
   * First minute offered (default: 0), then every `minuteInterval` after it.
   * Lets a picker step in whole intervals from another time, e.g. an end time
   * a whole number of quarter hours after an 8:16 start offers :01/:16/:31/:46.
   */
  minuteOffset?: number
  /** Accessible name for the trigger, replacing its visible text. */
  'aria-label'?: string
  /** Rendered after the time in the trigger, e.g. a "+1 day" marker. */
  suffix?: React.ReactNode
}

// Generate hours 1-12
const HOURS = Array.from({ length: 12 }, (_, i) => i + 1)

// Generate minutes based on interval
function generateMinutes(interval: number, offset = 0): number[] {
  const minutes: number[] = []
  for (let i = ((offset % interval) + interval) % interval; i < 60; i += interval) {
    minutes.push(i)
  }
  return minutes
}

const PERIODS: Period[] = ['AM', 'PM']

// Handle wheel events for scroll containers inside Radix scroll-locked contexts
function handleWheelScroll(e: React.WheelEvent<HTMLDivElement>) {
  const container = e.currentTarget
  container.scrollTop += e.deltaY
}

/**
 * TimePicker - Custom time picker with three-column scroll selection
 *
 * Features:
 * - Three scrollable columns: Hours | Minutes | AM/PM
 * - 12-hour format display
 * - Configurable minute intervals
 * - Auto-scrolls to selected values on open
 * - Matches DatePicker popover pattern
 */
export function TimePicker({
  value,
  onChange,
  disabled = false,
  placeholder = 'Select time',
  className,
  minuteInterval = 1,
  minuteOffset = 0,
  'aria-label': ariaLabel,
  suffix,
}: TimePickerProps) {
  const [open, setOpen] = React.useState(false)

  // Parse current value into components
  const parseValue = React.useCallback(() => {
    if (!value) {
      return { hour: 12, minute: 0, period: 'AM' as Period }
    }

    const [hoursStr, minutesStr] = value.split(':')
    const hours24 = parseInt(hoursStr, 10)
    const minutes = parseInt(minutesStr, 10)

    const period: Period = hours24 >= 12 ? 'PM' : 'AM'
    let hours12 = hours24 % 12
    if (hours12 === 0) hours12 = 12

    return { hour: hours12, minute: minutes, period }
  }, [value])

  const { hour: selectedHour, minute: selectedMinute, period: selectedPeriod } = parseValue()

  // Refs for scroll containers
  const hourScrollRef = React.useRef<HTMLDivElement>(null)
  const minuteScrollRef = React.useRef<HTMLDivElement>(null)
  const periodScrollRef = React.useRef<HTMLDivElement>(null)

  // Generate minutes array based on interval
  const minutes = React.useMemo(() => generateMinutes(minuteInterval, minuteOffset), [minuteInterval, minuteOffset])

  // Format display value
  const displayValue = value ? formatTime12Hour(value) : null

  // Convert selection to 24-hour format and call onChange
  const handleSelectionChange = (hour: number, minute: number, period: Period) => {
    let hours24 = hour
    if (period === 'AM' && hour === 12) {
      hours24 = 0
    } else if (period === 'PM' && hour !== 12) {
      hours24 = hour + 12
    }

    const timeStr = `${hours24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
    onChange(timeStr)
  }

  // Auto-scroll to selected values when popover opens
  React.useEffect(() => {
    if (open) {
      const scrollToSelected = (
        ref: React.RefObject<HTMLDivElement | null>,
        selector: string
      ) => {
        setTimeout(() => {
          const element = ref.current?.querySelector(selector)
          if (element) {
            element.scrollIntoView({ block: 'center' })
          }
        }, 0)
      }

      scrollToSelected(hourScrollRef, '[data-selected="true"]')
      scrollToSelected(minuteScrollRef, '[data-selected="true"]')
      scrollToSelected(periodScrollRef, '[data-selected="true"]')
    }
  }, [open])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            'w-full justify-start text-left font-normal',
            !value && 'text-muted-foreground',
            className
          )}
        >
          <Clock className="mr-2 h-4 w-4" />
          {displayValue ?? <span>{placeholder}</span>}
          {displayValue && suffix}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <div className="grid grid-cols-3 divide-x">
          {/* Hours Column */}
          <div className="flex flex-col">
            <div className="py-2 text-xs font-medium text-muted-foreground text-center border-b">
              Hour
            </div>
            <div
              ref={hourScrollRef}
              onWheel={handleWheelScroll}
              className="h-[180px] overflow-y-auto overscroll-contain p-1 scrollbar-always-visible"
            >
              {HOURS.map((hour) => {
                const isSelected = selectedHour === hour
                return (
                  <Button
                    key={hour}
                    type="button"
                    variant={isSelected ? 'default' : 'ghost'}
                    size="sm"
                    data-selected={isSelected}
                    className={cn(
                      'w-full justify-center font-normal tabular-nums h-8',
                      isSelected && 'font-medium'
                    )}
                    onClick={() => handleSelectionChange(hour, selectedMinute, selectedPeriod)}
                  >
                    {hour}
                  </Button>
                )
              })}
            </div>
          </div>

          {/* Minutes Column */}
          <div className="flex flex-col">
            <div className="py-2 text-xs font-medium text-muted-foreground text-center border-b">
              Min
            </div>
            <div
              ref={minuteScrollRef}
              onWheel={handleWheelScroll}
              className="h-[180px] overflow-y-auto overscroll-contain p-1 scrollbar-always-visible"
            >
              {minutes.map((minute) => {
                const isSelected = selectedMinute === minute
                return (
                  <Button
                    key={minute}
                    type="button"
                    variant={isSelected ? 'default' : 'ghost'}
                    size="sm"
                    data-selected={isSelected}
                    className={cn(
                      'w-full justify-center font-normal tabular-nums h-8',
                      isSelected && 'font-medium'
                    )}
                    onClick={() => handleSelectionChange(selectedHour, minute, selectedPeriod)}
                  >
                    {minute.toString().padStart(2, '0')}
                  </Button>
                )
              })}
            </div>
          </div>

          {/* AM/PM Column */}
          <div className="flex flex-col">
            <div className="py-2 text-xs font-medium text-muted-foreground text-center border-b">
              &nbsp;
            </div>
            <div
              ref={periodScrollRef}
              onWheel={handleWheelScroll}
              className="h-[180px] overflow-y-auto overscroll-contain p-1 scrollbar-always-visible"
            >
              {PERIODS.map((period) => {
                const isSelected = selectedPeriod === period
                return (
                  <Button
                    key={period}
                    type="button"
                    variant={isSelected ? 'default' : 'ghost'}
                    size="sm"
                    data-selected={isSelected}
                    className={cn(
                      'w-full justify-center font-normal h-8',
                      isSelected && 'font-medium'
                    )}
                    onClick={() => handleSelectionChange(selectedHour, selectedMinute, period)}
                  >
                    {period}
                  </Button>
                )
              })}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
